import * as dateFns from 'date-fns';
import * as React from 'react';
import { DayPicker, DayPickerProps, DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import OutsideClickHandler from 'react-outside-click-handler';
import { IDatePickerComponentProps } from './datepicker';
import { DatePickerNavbar } from './datepicker-navbar';
import * as S from './datepicker.styles';

interface IDatePickerRangeProps {
    /**
     * If renderComponent is passed, then we render popover with datepicker
     * If not, then we just render datepicker
     */
    renderComponent?: (props: IDatePickerComponentProps) => React.ReactElement;
    onDateRangeSelected?: (from: Date, to: Date) => void;
    format?: string;
    from?: Date;
    to?: Date;
}

interface IDatePickerRangeState {
    from: Date | undefined;
    to: Date | undefined;
    enteredTo: Date | undefined;
}

const defaultProps = {
    format: 'ddd, MMM dd, yyyy',
};

export class DatePickerRange extends React.Component<IDatePickerRangeProps, IDatePickerRangeState> {

    constructor(props: IDatePickerRangeProps) {
        super(props);
        this.state = this.getInitialState(props.from, props.to);
    }

    public render(): React.ReactElement {
        const { from, enteredTo } = this.state;
        const { format, renderComponent } = this.props;

        const selectedRange: DateRange | undefined = from ? {
            from,
            to: enteredTo,
        } : undefined;

        const disabledDays = from ? {
            before: from,
            // Forbid to select more than 1 year range
            after: dateFns.addYears(from, 1),
        } : undefined;

        const dayPickerProps: DayPickerProps = {
            mode: "range",
            selected: selectedRange,
            disabled: disabledDays,
            onSelect: this.handleSelect,
            onDayMouseEnter: this.handleDayMouseEnter,
            showOutsideDays: true,
            // navbarElement: this.renderNavbarElement, // Not available in v9
            defaultMonth: from,
        };


        return (
            <S.DatePickerWrapper>
                <OutsideClickHandler onOutsideClick={this.hideDayPicker}>
                    <DayPicker {...dayPickerProps} />
                </OutsideClickHandler>
            </S.DatePickerWrapper>
        );
    }

    private renderNavbarElement = (props: any): React.ReactElement => {
        const from = this.state.from ? dateFns.format(this.state.from, 'ddd, MMM dd, yyyy') : '';
        const to = this.state.to ? dateFns.format(this.state.to, 'ddd, MMM dd, yyyy') : '';
        const month = dateFns.format(props.month, 'MMMM yyyy');

        return (
            <DatePickerNavbar
                from={from}
                to={to}
                isShowRange={true}
                month={month}
                showPreviousButton={props.showPreviousButton}
                onPreviousClick={props.onPreviousClick}
                showNextButton={props.showNextButton}
                onNextClick={props.onNextClick}
            />
        );
    };


    private handleSelect = (range: DateRange | undefined): void => {
        if (!range?.from) {
            this.resetState();
            return;
        }

        const { from, to } = range;
        const day = to || from;
        if (to) {
            this.setState({
                from,
                to,
                enteredTo: to,
            });

            if (this.props.onDateRangeSelected) {
                this.props.onDateRangeSelected(from, to);
            }
        } else {
            this.setState({
                from,
                to: undefined,
                enteredTo: undefined,
            });
        }
    };

    private handleDayMouseEnter = (day: Date): void => {
        const { from, to } = this.state;
        if (from && !to) {
            this.setState({
                enteredTo: day,
            });
        }
    };

    private hideDayPicker = (): void => {
        // No longer needed with DayPicker only
    };

    private formatDate = (date: Date, format: string): string => {
        if (!this.state.from) {
            return '';
        }

        const formattedFrom = dateFns.format(this.state.from, format);
        const formattedTo = dateFns.format(date, format);
        return `${formattedFrom} - ${formattedTo}`;
    };

    private resetState = (): void => {
        this.setState(this.getInitialState());
    };

    private getInitialState = (from?: Date, to?: Date): IDatePickerRangeState => {
        return {
            from,
            to,
            enteredTo: to, // Keep track of the last day for mouseEnter.
        };
    };
}
